import PartnerEntity, { PartnerUpdate } from '../../entities/Partner';
import { getDomainFromWebsite } from '../core';
import { isEmpty, sanitize } from '../strings';
import type { ProviderResult } from './types';

export function hasValidAccountInfo(partner: PartnerEntity) {
  if (isEmpty(partner.name)) {
    return false;
  }

  if (isEmpty(partner.description)) {
    return false;
  }

  if (isEmpty(partner.phoneNumber)) {
    return false;
  }

  if (isEmpty(partner.email)) {
    return false;
  }

  return !!partner.address;
}

export function cleanData<T extends PartnerUpdate>(data: T): T {
  if (typeof data.email === 'string') {
    data.email = data.email.trim().toLowerCase();
  }

  if (typeof data.description === 'string') {
    data.description = sanitize(data.description);
  }

  return data;
}

export function providerResultToPartner({ provider, data }: ProviderResult) {
  switch (provider) {
  case 'GOOGLE_MAPS':
    return {
      name: data.name.trim(),
      email: null as string | null,
      website: data.websiteUri?.trim(),
      domain: data.websiteUri ? getDomainFromWebsite(data.websiteUri) : null,
      phoneNumber: data.internationalPhoneNumber?.trim(),
      isActive: data.isActive,
    };
  case 'TRIPADVISOR':
    return {
      name: data.data.names.find(name => name.primary)?.value?.trim() || data.data.names[0]?.value?.trim(),
      email: data.data.official_email?.trim(),
      website: data.data.urls.official?.trim(),
      phoneNumber: data.data.phone_numbers[0]?.value?.trim() || null,
      isActive: data.data.status.value !== 'CLOSED',
    };
  default:
    throw new Error(`Unknown provider: ${provider}`);
  }
}

export function providerResultToProviderInfo({ provider, data }: ProviderResult) {
  switch (provider) {
  case 'GOOGLE_MAPS':
    return {
      provider,
      providerId: data.id,
      url: data.googleMapsUri?.trim(),
      ratingAverage: data.rating,
      ratingCount: data.userRatingCount
    };
  case 'TRIPADVISOR':
    return {
      provider,
      providerId: data.data.id.toString(),
      url: data.data.urls.tripadvisor?.main?.trim(),
      ratingAverage: data.data.traveler_ratings.overall?.rating ?? 0,
      ratingCount: data.data.traveler_ratings.overall?.count ?? 0
    };
  default:
    throw new Error(`Unknown provider: ${provider}`);
  }
}

export function providerResultToReviews({ provider, data }: ProviderResult) {
  switch (provider) {
  case 'GOOGLE_MAPS':
    return data.reviews.map(review => ({
      author: review.author ?? 'Anonymous',
      comment: review.text,
      createdAt: review.publishedAt,
      provider,
      rating: review.rating,
    }));
  case 'TRIPADVISOR':
    return data.reviews.map(review => ({
      author: review.user.username ?? 'Anonymous',
      comment: review.text?.[0].value,
      createdAt: review.publish_ts,
      provider,
      rating: review.rating,
    }));
  default:
    return [];
  }
}

export function providerResultToPhotos({ provider, data }: ProviderResult) {
  switch (provider) {
  case 'GOOGLE_MAPS':
    return data.photos;
  case 'TRIPADVISOR':
    return data.photos.map(photo => photo.photo.original_size_url);
  default:
    return [];
  }
}

export function providerResultToAddress({ provider, data }: ProviderResult) {
  switch (provider) {
  case 'GOOGLE_MAPS':
    return {
      street: data.address.street ?? '',
      city: data.address.city ?? '',
      country: data.address.country ?? '',
      countryCode: data.address.countryCode ?? '',
      lat: data.address.latitude,
      long: data.address.longitude,
      postalCode: data.address.postalCode ?? '',
    };
  case 'TRIPADVISOR':
    return {
      street: data.data.addresses[0]?.street_address ?? '',
      city: data.data.addresses[0]?.city ?? '',
      country: data.data.addresses[0]?.country_name ?? '',
      countryCode: data.data.addresses[0]?.country_code ?? '',
      lat: data.data.coordinates?.latitude,
      long: data.data.coordinates?.longitude,
      postalCode: data.data.addresses[0]?.postal_code ?? '',
    };
  default:
    throw new Error(`Unknown provider: ${provider}`);
  }
}